import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  RefreshCw,
  ShoppingBag,
  Users,
  UtensilsCrossed,
} from "lucide-react";
import { useState } from "react";

import { Button, Card, CardHeader, Money, StatusPill } from "@/components/ui";
import { getSession } from "@/lib/session";
import {
  createTable,
  createZone,
  getFloorMap,
  openCheck,
  TABLE_STATUS_LABEL,
  TABLE_STATUS_TONE,
  updateTableStatus,
  type FloorMapTable,
  type TableStatus,
} from "@/lib/restaurant";

interface Props {
  onOpenCheck: (checkId: string) => void;
}

export function FloorMapScreen({ onOpenCheck }: Props) {
  const queryClient = useQueryClient();
  const session = getSession();
  const branchId = session?.branches?.[0]?.id ?? "";

  const [activeZoneId, setActiveZoneId] = useState<string | "all">("all");
  const [selectedTable, setSelectedTable] = useState<FloorMapTable | null>(null);
  const [showNewTableModal, setShowNewTableModal] = useState(false);
  const [showNewZoneModal, setShowNewZoneModal] = useState(false);
  const [showOpenCheckModal, setShowOpenCheckModal] = useState(false);

  // Form states
  const [newTableCode, setNewTableCode] = useState("");
  const [newTableSeats, setNewTableSeats] = useState(4);
  const [newTableZoneId, setNewTableZoneId] = useState<string>("");
  const [newZoneName, setNewZoneName] = useState("");
  const [checkGuests, setCheckGuests] = useState(2);
  const [checkLabel, setCheckLabel] = useState("");

  const floorMapQuery = useQuery({
    queryKey: ["restaurant", "floor-map", branchId],
    queryFn: () => getFloorMap(branchId),
    enabled: Boolean(branchId),
    refetchInterval: 10_000,
  });

  const createTableMutation = useMutation({
    mutationFn: () =>
      createTable({
        branch_id: branchId,
        code: newTableCode,
        seats: newTableSeats,
        zone_id: newTableZoneId || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["restaurant", "floor-map"] });
      setShowNewTableModal(false);
      setNewTableCode("");
    },
  });

  const createZoneMutation = useMutation({
    mutationFn: () =>
      createZone({
        branch_id: branchId,
        name: newZoneName,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["restaurant", "floor-map"] });
      setShowNewZoneModal(false);
      setNewZoneName("");
    },
  });

  const openCheckMutation = useMutation({
    mutationFn: (tableId?: string | null) =>
      openCheck({
        branch_id: branchId,
        table_id: tableId ?? null,
        guests: checkGuests,
        label: checkLabel || undefined,
      }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["restaurant"] });
      setShowOpenCheckModal(false);
      setSelectedTable(null);
      setCheckLabel("");
      onOpenCheck(res.check.id);
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ tableId, status }: { tableId: string; status: TableStatus }) =>
      updateTableStatus(tableId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["restaurant", "floor-map"] });
      setSelectedTable(null);
    },
  });

  const zones = floorMapQuery.data?.zones ?? [];
  const unassigned = floorMapQuery.data?.unassigned_tables ?? [];

  // Todas las mesas para cálculo de métricas
  const allTables: FloorMapTable[] = [
    ...zones.flatMap((z) => z.tables),
    ...unassigned,
  ];

  const libres = allTables.filter((t) => t.status === "available").length;
  const ocupadas = allTables.filter((t) => t.status === "occupied" || t.status === "bill_printed").length;
  const totalCuentas = allTables.reduce((acc, t) => acc + (t.total_cents || 0), 0);

  const displayTables =
    activeZoneId === "all"
      ? allTables
      : zones.find((z) => z.id === activeZoneId)?.tables ?? [];

  return (
    <div className="space-y-6">
      {/* Resumen superior */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Card className="p-4">
          <p className="text-muted text-xs font-medium">Mesas en total</p>
          <p className="text-carbon text-2xl font-bold">{allTables.length}</p>
        </Card>
        <Card className="p-4">
          <p className="text-muted text-xs font-medium">Mesas libres</p>
          <p className="text-success text-2xl font-bold">{libres}</p>
        </Card>
        <Card className="p-4">
          <p className="text-muted text-xs font-medium">Ocupadas / En cuenta</p>
          <p className="text-warning text-2xl font-bold">{ocupadas}</p>
        </Card>
        <Card className="p-4">
          <p className="text-muted text-xs font-medium">Consumo activo en sala</p>
          <p className="text-electric-text text-2xl font-bold">
            <Money cents={totalCuentas} />
          </p>
        </Card>
      </div>

      {/* Barra de zonas y botones */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          <button
            type="button"
            onClick={() => setActiveZoneId("all")}
            className={`rounded-msr px-4 py-2 text-sm font-medium transition-colors ${
              activeZoneId === "all"
                ? "bg-electric text-on-electric shadow-sm"
                : "border-line bg-snow text-carbon hover:bg-line/50 border"
            }`}
          >
            Todas las salas ({allTables.length})
          </button>
          {zones.map((zone) => (
            <button
              key={zone.id}
              type="button"
              onClick={() => setActiveZoneId(zone.id)}
              className={`rounded-msr px-4 py-2 text-sm font-medium transition-colors ${
                activeZoneId === zone.id
                  ? "bg-electric text-on-electric shadow-sm"
                  : "border-line bg-snow text-carbon hover:bg-line/50 border"
              }`}
            >
              {zone.name} ({zone.tables.length})
            </button>
          ))}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setShowNewZoneModal(true)}
            icon={<Plus className="size-3.5" />}
          >
            Nueva Zona
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setShowOpenCheckModal(true)}
            icon={<ShoppingBag className="size-4" />}
          >
            Comanda rápida / Para llevar
          </Button>
          <Button
            size="sm"
            onClick={() => setShowNewTableModal(true)}
            icon={<Plus className="size-4" />}
          >
            Nueva Mesa
          </Button>
        </div>
      </div>

      {/* Plano de mesas en cuadrícula interactiva */}
      {floorMapQuery.isLoading ? (
        <div className="text-muted flex h-64 items-center justify-center">
          <RefreshCw className="mr-2 size-5 animate-spin" />
          Cargando plano de mesas...
        </div>
      ) : displayTables.length === 0 ? (
        <Card className="flex flex-col items-center justify-center p-12 text-center">
          <UtensilsCrossed className="text-muted/50 mb-3 size-12" />
          <h3 className="text-carbon text-base font-semibold">No hay mesas registradas en esta zona</h3>
          <p className="text-muted mt-1 text-sm">
            Agrega tu primera mesa para comenzar a comandar.
          </p>
          <Button
            className="mt-4"
            size="sm"
            onClick={() => setShowNewTableModal(true)}
            icon={<Plus className="size-4" />}
          >
            Agregar Mesa
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
          {displayTables.map((table) => {
            const isOcc = table.status === "occupied" || table.status === "bill_printed";
            return (
              <button
                key={table.id}
                type="button"
                onClick={() => setSelectedTable(table)}
                className={`group bg-snow text-carbon relative flex flex-col justify-between rounded-xl border p-4 text-left shadow-sm transition-all hover:scale-[1.02] active:scale-[0.98] ${
                  table.status === "available"
                    ? "border-emerald-500/30 hover:border-emerald-500/60"
                    : table.status === "bill_printed"
                    ? "border-amber-500/50 bg-amber-500/5 hover:border-amber-500"
                    : table.status === "occupied"
                    ? "border-rose-500/40 bg-rose-500/5 hover:border-rose-500"
                    : "border-line hover:border-electric"
                }`}
              >
                <div className="flex items-start justify-between gap-1">
                  <div>
                    <span className="text-carbon font-mono text-lg font-bold">
                      {table.code}
                    </span>
                    <div className="text-muted flex items-center gap-1 text-xs">
                      <Users className="size-3" />
                      <span>{table.seats} pers.</span>
                    </div>
                  </div>
                  <StatusPill tone={TABLE_STATUS_TONE[table.status]}>
                    {TABLE_STATUS_LABEL[table.status]}
                  </StatusPill>
                </div>

                <div className="border-line mt-4 border-t pt-3">
                  {isOcc ? (
                    <div>
                      <p className="text-muted text-xs">
                        {table.active_checks_count} comanda(s)
                      </p>
                      <p className="text-carbon font-mono text-sm font-semibold">
                        <Money cents={table.total_cents} />
                      </p>
                    </div>
                  ) : (
                    <p className="text-success text-xs font-medium">Disponible</p>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Modal / Menú de Acción de Mesa Seleccionada */}
      {selectedTable && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
          <Card className="w-full max-w-md space-y-5 p-6 shadow-2xl">
            <CardHeader
              title={`Mesa ${selectedTable.code}`}
              description={`Capacidad: ${selectedTable.seats} comensales | Estado actual: ${TABLE_STATUS_LABEL[selectedTable.status]}`}
            />

            <div className="space-y-3">
              {selectedTable.status === "available" ? (
                <div className="space-y-4">
                  <p className="text-muted text-sm">
                    La mesa está disponible para recibir comensales.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-muted text-xs font-medium">Comensales</label>
                      <input
                        type="number"
                        min="1"
                        max="30"
                        value={checkGuests}
                        onChange={(e) => setCheckGuests(Number(e.target.value))}
                        className="border-line bg-surface text-carbon focus:border-electric mt-1 h-10 w-full rounded-msr border px-3 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-muted text-xs font-medium">Nombre / Referencia</label>
                      <input
                        type="text"
                        placeholder="Ej. Familia López"
                        value={checkLabel}
                        onChange={(e) => setCheckLabel(e.target.value)}
                        className="border-line bg-surface text-carbon focus:border-electric mt-1 h-10 w-full rounded-msr border px-3 focus:outline-none"
                      />
                    </div>
                  </div>
                  <Button
                    className="w-full"
                    size="lg"
                    onClick={() => openCheckMutation.mutate(selectedTable.id)}
                    disabled={openCheckMutation.isPending}
                    icon={<UtensilsCrossed className="size-4" />}
                  >
                    Abrir Comanda
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="border-line bg-surface rounded-msr border p-3">
                    <p className="text-muted text-xs">Total acumulado en mesa:</p>
                    <p className="text-carbon text-xl font-bold">
                      <Money cents={selectedTable.total_cents} />
                    </p>
                  </div>

                  <Button
                    className="w-full"
                    size="lg"
                    onClick={async () => {
                      const checks = await queryClient.fetchQuery({
                        queryKey: ["restaurant", "checks", "table", selectedTable.id],
                        queryFn: async () => {
                          const res = await fetch(
                            `/api/v1/restaurant/checks?table_id=${selectedTable.id}&statuses=open&statuses=billed`,
                            { headers: { Authorization: `Bearer ${session?.tokens?.access_token}` } }
                          );
                          return res.json();
                        },
                      });
                      if (checks?.items?.[0]?.id) {
                        onOpenCheck(checks.items[0].id);
                      } else {
                        openCheckMutation.mutate(selectedTable.id);
                      }
                    }}
                    icon={<UtensilsCrossed className="size-4" />}
                  >
                    Ver / Modificar Comanda
                  </Button>

                  <Button
                    className="w-full"
                    variant="secondary"
                    onClick={() => {
                      openCheckMutation.mutate(selectedTable.id);
                    }}
                    icon={<Plus className="size-4" />}
                  >
                    Abrir 2ª cuenta separada en esta mesa
                  </Button>
                </div>
              )}

              {/* Cambiar estado manual */}
              <div className="border-line border-t pt-4">
                <p className="text-muted mb-2 text-xs font-semibold">Cambiar estado de la mesa:</p>
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      updateStatusMutation.mutate({
                        tableId: selectedTable.id,
                        status: "available",
                      })
                    }
                  >
                    Libre
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      updateStatusMutation.mutate({
                        tableId: selectedTable.id,
                        status: "dirty",
                      })
                    }
                  >
                    Por limpiar
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      updateStatusMutation.mutate({
                        tableId: selectedTable.id,
                        status: "reserved",
                      })
                    }
                  >
                    Reservada
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button variant="ghost" onClick={() => setSelectedTable(null)}>
                Cerrar
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Modal: Nueva Mesa */}
      {showNewTableModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
          <Card className="w-full max-w-sm space-y-4 p-6 shadow-2xl">
            <CardHeader title="Nueva Mesa" description="Registrar mesa en el plano de piso" />
            <div className="space-y-3">
              <div>
                <label className="text-muted text-xs font-medium">Código / Nombre de Mesa *</label>
                <input
                  type="text"
                  placeholder="Ej. M-01, Barra 3, T-04"
                  value={newTableCode}
                  onChange={(e) => setNewTableCode(e.target.value)}
                  className="border-line bg-surface text-carbon focus:border-electric mt-1 h-10 w-full rounded-msr border px-3 focus:outline-none"
                />
              </div>
              <div>
                <label className="text-muted text-xs font-medium">Zona / Salón</label>
                <select
                  value={newTableZoneId}
                  onChange={(e) => setNewTableZoneId(e.target.value)}
                  className="border-line bg-surface text-carbon focus:border-electric mt-1 h-10 w-full rounded-msr border px-3 focus:outline-none"
                >
                  <option value="">Sin zona asignada</option>
                  {zones.map((z) => (
                    <option key={z.id} value={z.id}>
                      {z.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-muted text-xs font-medium">Número de Asientos</label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={newTableSeats}
                  onChange={(e) => setNewTableSeats(Number(e.target.value))}
                  className="border-line bg-surface text-carbon focus:border-electric mt-1 h-10 w-full rounded-msr border px-3 focus:outline-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowNewTableModal(false)}>
                Cancelar
              </Button>
              <Button
                onClick={() => createTableMutation.mutate()}
                disabled={!newTableCode || createTableMutation.isPending}
              >
                Guardar Mesa
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Modal: Nueva Zona */}
      {showNewZoneModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
          <Card className="w-full max-w-sm space-y-4 p-6 shadow-2xl">
            <CardHeader title="Nueva Zona" description="Ej. Terraza, Planta Baja, Barra, VIP" />
            <div>
              <label className="text-muted text-xs font-medium">Nombre de la Zona *</label>
              <input
                type="text"
                placeholder="Ej. Salón Principal"
                value={newZoneName}
                onChange={(e) => setNewZoneName(e.target.value)}
                className="border-line bg-surface text-carbon focus:border-electric mt-1 h-10 w-full rounded-msr border px-3 focus:outline-none"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowNewZoneModal(false)}>
                Cancelar
              </Button>
              <Button
                onClick={() => createZoneMutation.mutate()}
                disabled={!newZoneName || createZoneMutation.isPending}
              >
                Crear Zona
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Modal: Comanda rápida sin mesa */}
      {showOpenCheckModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
          <Card className="w-full max-w-sm space-y-4 p-6 shadow-2xl">
            <CardHeader
              title="Comanda para Llevar / Barra"
              description="Cuenta directa sin asignación de mesa fija"
            />
            <div className="space-y-3">
              <div>
                <label className="text-muted text-xs font-medium">Referencia o Nombre del Cliente</label>
                <input
                  type="text"
                  placeholder="Ej. Para llevar - Carlos R."
                  value={checkLabel}
                  onChange={(e) => setCheckLabel(e.target.value)}
                  className="border-line bg-surface text-carbon focus:border-electric mt-1 h-10 w-full rounded-msr border px-3 focus:outline-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowOpenCheckModal(false)}>
                Cancelar
              </Button>
              <Button
                onClick={() => openCheckMutation.mutate(null)}
                disabled={openCheckMutation.isPending}
              >
                Abrir Cuenta
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
